import NotificationsActiveIcon from "@mui/icons-material/NotificationsActiveOutlined";
import { Button } from "@mui/material";
import { isEmpty } from "lodash-es";
import { useCallback, type FC } from "react";
import { useFlags } from "../../../../../server/optimizely";
import { useSupabaseLoader } from "../../../../../server/supabase/hooks";
import { useDialog } from "../../../../../utils/hooks/use-dialog";
import type { UUID } from "../../../../../utils/uuid";
import type { FollowUpMomentDetails } from "../../../../follow-up/server/types";
import { CreateFollowUpDialog } from "./create-follow-up-dialog";
import { ShowFollowUpMomentsDialog } from "./show-follow-up-moments-dialog";

export const MomentFormCreateFollowUpButton: FC<{
  momentId: UUID;
  saving?: boolean;
}> = ({ momentId, saving }) => {
  const createNewDialog = useDialog<FollowUpMomentDetails>();
  const momentsDialog = useDialog();

  const {
    flags: { enable_follow_up_page },
  } = useFlags("enable_follow_up_page");

  const { data: existing, loading: existingLoading } = useSupabaseLoader(
    async ({ supabase, account_id }) => {
      const { data, error } = await supabase
        .rpc("moment_existing_follow_ups", {
          account_id_: account_id,
          moment_id_: momentId,
        })
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    [momentId],
  );

  const { handleOpen: createNewOpen } = createNewDialog;
  const { handleClose: existingClose } = momentsDialog;

  const loadFollowUpData = useCallback(
    (details: FollowUpMomentDetails) => {
      if (isEmpty(details.surveys)) {
        return window.open(`/follow-up/moment/${details.id}`, "_blank");
      }

      existingClose();
      return createNewOpen(details);
    },
    [createNewOpen, existingClose],
  );

  if (!enable_follow_up_page) return null;

  return (
    <>
      <Button
        data-analytics-id="moment-form-create-follow-up-button"
        disabled={false}
        loading={existingLoading || saving}
        variant="outlined"
        onClick={() =>
          existing
            ? momentsDialog.handleOpen()
            : window.open(`/follow-up/moment/${momentId}`, "_blank")
        }
        sx={{ ml: 1 }}
        startIcon={<NotificationsActiveIcon />}
      >
        {`Follow-Up${!isEmpty(existing) ? ` (${existing.follow_ups?.length})` : ""}`}
      </Button>
      {existing && (
        <ShowFollowUpMomentsDialog
          open={momentsDialog.open}
          onClose={momentsDialog.handleClose}
          data={existing}
          onCreateFollowUp={loadFollowUpData}
        />
      )}
      {createNewDialog.data && (
        <CreateFollowUpDialog
          open={createNewDialog.open}
          onClose={() => {
            createNewDialog.handleClose();
            momentsDialog.handleOpen();
          }}
          data={createNewDialog.data}
        />
      )}
    </>
  );
};
